import { useState } from 'react';
import { ratingApi } from '../../api/client';
import { Button, Card, Chip, Field, Notice } from '../../components/ui';
import { shortWhen } from '../../lib/format';
import { useData } from '../../lib/useData';

/**
 * Stitch Admin → System → Rating (Billing MVP C, 1.13.0, Teampace 3097; docs/rating.md): whether the
 * stitch-rating container answers, where, and how far behind the usage ledger it is. Its address is
 * found by its EasyPanel name; "Where it is" is only for when that doesn't find it. Its key is made
 * from DATA_ENCRYPTION_KEY, so there is nothing to paste.
 */
export function RatingCard() {
  const view = useData(() => ratingApi.view(), [], { live: false });
  const [address, setAddress] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null);
  const v = view.data;
  const h = v?.health;
  const s = v?.status;

  async function save(next: string | null) {
    setBusy(true);
    setSaid(null);
    try {
      await ratingApi.setAddress(next);
      setAddress(null);
      view.reload();
      setSaid({ tone: 'ok', text: next ? 'Saved. Stitch tries this address first.' : 'Cleared. Stitch looks for it by its EasyPanel name.' });
    } catch (err) {
      setSaid({ tone: 'crit', text: err instanceof Error ? err.message : 'That didn’t work.' });
    } finally {
      setBusy(false);
    }
  }

  const chip = !v ? undefined : !v.set ? <Chip tone="warn">Not set up</Chip> : h?.ok ? <Chip tone="ok">Answering</Chip> : <Chip tone="crit">Not answering</Chip>;
  return (
    <Card title="Rating" action={chip}>
      <div className="card-body stack">
        <p className="muted small">
          The stitch-rating container prices what companies use (toll-free minutes, texts, Stitch Live minutes over their allowance) for invoices, in its own database. Until
          it has counted a month, that month’s invoices wait instead of going out without usage.
        </p>
        {v && !v.set && <Notice tone="warn">DATA_ENCRYPTION_KEY isn’t set here, so the portal can’t call the rating service.</Notice>}
        {v?.set && (
          <dl className="facts">
            <dt>Rating service</dt>
            <dd>
              {h?.ok && h.url
                ? `Running${h.body?.version ? ` (${h.body.version})` : ''} at ${h.url}`
                : 'Not answering: invoices made now have no usage, and wait. Deploy it (docs/deploy-easypanel.md → Rating), or say where it is below.'}
            </dd>
            {s && (
              <>
                <dt>Usage counted</dt>
                <dd>
                  {s.events.toLocaleString('en-US')} items{s.lastEventAt ? `, the latest ${shortWhen(s.lastEventAt)}` : ''}
                </dd>
                <dt>Waiting</dt>
                <dd>
                  {s.queued === 0 && s.behind === 0
                    ? 'Nothing: caught up'
                    : `${s.queued.toLocaleString('en-US')} to price, ${s.behind.toLocaleString('en-US')} not yet read from Stitch`}
                </dd>
              </>
            )}
          </dl>
        )}
        {said && <Notice tone={said.tone}>{said.text}</Notice>}
        {v?.set && (
          <form
            className="stack"
            onSubmit={(e) => {
              e.preventDefault();
              const next = (address ?? v.address ?? '').trim();
              void save(next || null);
            }}
          >
            <Field
              label="Where it is (optional)"
              placeholder="http://stitch_stitch-rating:4300"
              value={address ?? v.address ?? ''}
              onChange={(e) => setAddress(e.target.value)}
              spellCheck={false}
              hint="Only if Stitch doesn’t find it by its EasyPanel name. Inside the server’s network; no domain needed."
            />
            <div className="row-actions">
              <Button type="submit" disabled={busy || address === null}>
                Save
              </Button>
              {v.address && (
                <Button type="button" variant="ghost" disabled={busy} onClick={() => void save(null)}>
                  Clear
                </Button>
              )}
            </div>
          </form>
        )}
      </div>
    </Card>
  );
}
